import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  Copy,
  Mail,
  MessageSquare,
  Share2,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Skeleton } from "./ui/skeleton";
import AssetSelectionDialog from "./AssetSelectionDialog";
import AssetSelectionDropdown from "./AssetSelectionDropdown";
import { cn } from "../lib/utils";
import { useIsMobile } from "../hooks/use-mobile";
import { useToast } from "../hooks/use-toast";
import {
  useCryptoAssets,
  useFetchPairs,
  useTradeRate,
} from "../hooks/query/queries";
import { truncateDecimals } from "../utils/numberFormatter";
import { formatZarDisplay } from "../services/currencyFormatterService";
import { useCreatePayLink } from "../hooks/query/dposQueries";
import { useMyBusinessAccountRequests } from "../hooks/query/kybQueries";
import {
  initialsFromBusinessLabel,
  pickBusinessNameFromAccountRequests,
} from "../utils/businessAccountRequestDisplay";
import whatsappIcon from "../assets/images/support/whatsapp.png";
import coinsIcon from "../assets/images/dashboard/coins.svg";
import CardSpinningLoader from "./CardSpinningLoader";
import PaymentFailed from "./paymentFailed";
import type { SplitPaymentLaunchContext } from "../types/paymentLinksPanel";

const SPLIT_PRESET_PERCENTS = [5, 10, 25, 50, 75] as const;

function zarStringToCents(raw: string): number {
  const digits = raw.replace(/[^\d.]/g, "");
  if (!digits || digits === ".") {
    return 0;
  }
  const [wholePart, fracPart = ""] = digits.split(".", 2);
  const whole = parseInt(wholePart || "0", 10);
  if (!Number.isFinite(whole) || whole < 0) {
    return 0;
  }
  const fracDigitsOnly = fracPart.replace(/\D/g, "");
  const fracTwo = (fracDigitsOnly + "00").slice(0, 2);
  const frac = parseInt(fracTwo, 10) || 0;
  return whole * 100 + frac;
}

function centsToZarAmountString(cents: number): string {
  const c = Math.max(0, Math.trunc(Math.round(cents)));
  const whole = Math.trunc(c / 100);
  const frac = c % 100;
  return `${whole}.${frac.toString().padStart(2, "0")}`;
}

function zarDecimalToCents(value: number): number {
  if (!Number.isFinite(value) || value < 0) {
    return 0;
  }
  return Math.round(value * 100 + Number.EPSILON);
}

function amountFromPercentOfTotal(totalZar: number, percent: number): string {
  const totalCents = zarDecimalToCents(totalZar);
  const splitCents = Math.round((totalCents * percent) / 100);
  return centsToZarAmountString(splitCents);
}

export type NewPayLinkWizardStep =
  | "form"
  | "amount"
  | "loading"
  | "review"
  | "failed"
  | "share";

export interface PayLinkCryptoAsset {
  assetId: string;
  asset: string;
  assetName?: string;
  assetIcon?: string;
  type?: string;
  status?: string;
  [key: string]: unknown;
}

const shareGradientIcon =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-[#57249C] to-[#EB3366] text-white";

function parseAmountInput(raw: string): number {
  const n = parseFloat(raw.replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function deriveCryptoAmount(
  amountNum: number,
  inputInCrypto: boolean,
  rate: number | undefined
): number {
  if (!rate || amountNum <= 0) {
    return inputInCrypto ? amountNum : 0;
  }
  return inputInCrypto ? amountNum : amountNum / rate;
}

function formatCryptoLine(n: number, symbol: string): string {
  const upper = symbol.toUpperCase();
  const dec = upper === "BTC" ? 6 : 4;
  return `${truncateDecimals(n, dec)} ${upper}`;
}

function displayName(first: string, last: string): string {
  const t = `${first} ${last}`.trim();
  return t || "Steve Brosome";
}

function isValidNameValue(value: string): boolean {
  // Allow letters/numbers and common real-name separators.
  return /^[a-z0-9]+(?:[ '-][a-z0-9]+)*$/i.test(value.trim());
}

function RequestingFromField({
  businessName,
  showSkeleton,
}: {
  businessName: string;
  showSkeleton: boolean;
}) {
  const initials = initialsFromBusinessLabel(businessName);
  return (
    <div className="w-full space-y-2">
      <span className="block text-[14px] leading-[20px] text-[#6D6584]">
        Requesting from:
      </span>
      {showSkeleton ? (
        <Skeleton className="h-[70px] w-full rounded-2xl md:h-[88px]" />
      ) : (
        <div className="flex h-[70px] items-center gap-3 rounded-2xl border border-[#CECBD7] px-4 py-3 md:h-[88px]">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-[#57249C] to-[#EB3366] text-sm font-semibold text-white"
            aria-hidden
          >
            {initials}
          </div>
          <p className="min-w-0 flex-1 truncate text-[16px] font-semibold leading-[24px] text-[#423E51]">
            {businessName}
          </p>
        </div>
      )}
    </div>
  );
}

export interface NewPayLinkWizardProps {
  step: NewPayLinkWizardStep;
  onStepChange: (step: NewPayLinkWizardStep) => void;
  onClose: () => void;
  businessId: string | null;
  fallbackBusinessName?: string | null;
  firstName: string;
  onFirstNameChange: (v: string) => void;
  lastName: string;
  onLastNameChange: (v: string) => void;
  reference: string;
  onReferenceChange: (v: string) => void;
  selectedAsset: PayLinkCryptoAsset | null;
  onSelectAsset: (asset: PayLinkCryptoAsset | null) => void;
  amount: string;
  onAmountChange: (v: string) => void;
  splitPaymentContext?: SplitPaymentLaunchContext | null;
}

export function NewPayLinkWizard({
  step,
  onStepChange,
  onClose,
  businessId,
  fallbackBusinessName = null,
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  reference,
  onReferenceChange,
  selectedAsset,
  onSelectAsset,
  amount,
  onAmountChange,
  splitPaymentContext = null,
}: NewPayLinkWizardProps) {
  const { toast } = useToast();
  const createPayLinkMutation = useCreatePayLink(businessId);
  const [createdPayLinkUrl, setCreatedPayLinkUrl] = useState<string | null>(
    null
  );
  const [inputInCrypto, setInputInCrypto] = useState(true);
  const isMobile = useIsMobile();
  const [assetSelectOpen, setAssetSelectOpen] = useState(false);
  const [assetSearchTerm, setAssetSearchTerm] = useState("");
  const [amountPhase, setAmountPhase] = useState<"asset" | "details">("asset");
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [showAmountError, setShowAmountError] = useState(false);
  const prevWizardStepRef = useRef<NewPayLinkWizardStep>(step);
  const splitSeedAppliedRef = useRef(false);
  const [splitPercentChoice, setSplitPercentChoice] = useState<
    number | "custom"
  >(10);

  const { data: accountRequests = [], isLoading: accountRequestsLoading } =
    useMyBusinessAccountRequests();

  const resolvedBusinessName = useMemo(() => {
    const fromApi = pickBusinessNameFromAccountRequests(
      accountRequests,
      businessId
    );
    if (fromApi) {
      return fromApi;
    }
    const fb = fallbackBusinessName?.trim();
    if (fb) {
      return fb;
    }
    return "Your business";
  }, [accountRequests, businessId, fallbackBusinessName]);

  const showRequestingSkeleton =
    accountRequestsLoading &&
    !pickBusinessNameFromAccountRequests(accountRequests, businessId) &&
    !fallbackBusinessName?.trim();

  const { data: cryptoAssets = [], isLoading: cryptoLoading } =
    useCryptoAssets();

  const assetsForDropdown = useMemo(() => {
    const list = (cryptoAssets as PayLinkCryptoAsset[]) || [];
    const filtered = list.filter((a) => a.status !== "Disabled");
    return filtered.length > 0 ? filtered : list;
  }, [cryptoAssets]);

  const filteredPayLinkAssets = useMemo(() => {
    const q = assetSearchTerm.trim().toLowerCase();
    if (!q) {
      return assetsForDropdown;
    }
    return assetsForDropdown.filter((a) => {
      const sym = (a.asset ?? "").toLowerCase();
      const name = (a.assetName ?? "").toLowerCase();
      return sym.includes(q) || name.includes(q);
    });
  }, [assetsForDropdown, assetSearchTerm]);

  const { data: assetPairs = [] } = useFetchPairs(selectedAsset?.assetId ?? "");

  const fiatPair = useMemo(() => {
    if (!assetPairs?.length) {
      return null;
    }
    const list = assetPairs as {
      pair: string;
      type?: string;
      availableAmount?: number;
      asset?: string;
    }[];
    const fiat = list.find((p) => p.type === "Fiat");
    return fiat ?? list[0];
  }, [assetPairs]);

  const { data: tradeRate, isPending: rateLoading } = useTradeRate(
    selectedAsset?.assetId,
    "buy",
    fiatPair?.pair,
    false
  );

  const rate = useMemo(() => {
    const r = tradeRate?.rate;
    if (r === undefined || r === null) {
      return undefined;
    }
    const n = typeof r === "number" ? r : parseFloat(String(r));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [tradeRate]);

  useEffect(() => {
    const prev = prevWizardStepRef.current;
    prevWizardStepRef.current = step;
    if (step === "amount" && prev === "form") {
      setAmountPhase("asset");
    }
  }, [step]);

  useEffect(() => {
    if (!splitPaymentContext) {
      splitSeedAppliedRef.current = false;
    }
  }, [splitPaymentContext]);

  useEffect(() => {
    if (step !== "amount" || !splitPaymentContext) {
      return;
    }
    if (splitSeedAppliedRef.current) {
      return;
    }
    splitSeedAppliedRef.current = true;
    setInputInCrypto(false);
    const defaultPct = 10;
    onAmountChange(
      amountFromPercentOfTotal(splitPaymentContext.totalZar, defaultPct)
    );
    setSplitPercentChoice(defaultPct);
  }, [step, splitPaymentContext, onAmountChange]);

  useEffect(() => {
    if (step !== "amount") {
      setAssetSearchTerm("");
      setAssetSelectOpen(false);
      return;
    }
    if (!cryptoLoading && assetsForDropdown.length > 0 && !selectedAsset) {
      const first = assetsForDropdown[0];
      if (first?.assetId) {
        onSelectAsset(first);
      }
    }
  }, [step, cryptoLoading, assetsForDropdown, selectedAsset, onSelectAsset]);

  const prevAssetIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedAsset?.assetId) {
      return;
    }
    if (
      prevAssetIdRef.current &&
      prevAssetIdRef.current !== selectedAsset.assetId
    ) {
      setInputInCrypto(true);
      onAmountChange("");
    }
    prevAssetIdRef.current = selectedAsset.assetId;
  }, [selectedAsset?.assetId, onAmountChange]);

  const amountNum = parseAmountInput(amount);

  const fiatZarCents = useMemo(() => {
    if (inputInCrypto) {
      if (!rate || amountNum <= 0) {
        return 0;
      }
      return Math.round(amountNum * rate * 100 + Number.EPSILON);
    }
    return zarStringToCents(amount);
  }, [inputInCrypto, rate, amountNum, amount]);

  const fiatZar = useMemo(
    () =>
      fiatZarCents <= 0 ? 0 : parseFloat(centsToZarAmountString(fiatZarCents)),
    [fiatZarCents]
  );

  const canContinueForm =
    firstName.trim().length > 0 && lastName.trim().length > 0;
  const firstNameErrorMessage = useMemo(() => {
    if (!showFormErrors) {
      return "";
    }
    const trimmed = firstName.trim();
    if (!trimmed) {
      return "Please enter first name";
    }
    if (!isValidNameValue(trimmed)) {
      return "First name can include letters, numbers, spaces, hyphens, and apostrophes";
    }
    return "";
  }, [showFormErrors, firstName]);

  const lastNameErrorMessage = useMemo(() => {
    if (!showFormErrors) {
      return "";
    }
    const trimmed = lastName.trim();
    if (!trimmed) {
      return "Please enter last name";
    }
    if (!isValidNameValue(trimmed)) {
      return "Last name can include letters, numbers, spaces, hyphens, and apostrophes";
    }
    return "";
  }, [showFormErrors, lastName]);

  const cryptoAmt = deriveCryptoAmount(amountNum, inputInCrypto, rate);

  const approxLabel = useMemo(() => {
    if (amountNum <= 0 || !rate) {
      return inputInCrypto
        ? formatZarDisplay(0)
        : formatCryptoLine(0, selectedAsset?.asset ?? "");
    }
    if (inputInCrypto) {
      return formatZarDisplay(fiatZar);
    }
    return formatCryptoLine(cryptoAmt, selectedAsset?.asset ?? "");
  }, [
    amountNum,
    rate,
    inputInCrypto,
    fiatZar,
    cryptoAmt,
    selectedAsset?.asset,
  ]);

  const canContinueAmount =
    Boolean(selectedAsset) &&
    Boolean(fiatPair?.pair) &&
    !rateLoading &&
    rate !== undefined;

  const getAmountValidationMessage = (): string => {
    if (amountNum <= 0) {
      return "Please enter an amount to receive";
    }
    if (fiatZarCents < 1) {
      return "Minimum amount is R0.01";
    }
    if (splitPaymentContext) {
      const capCents = zarDecimalToCents(splitPaymentContext.totalZar);
      if (fiatZarCents > capCents) {
        return `Amount cannot exceed ${splitPaymentContext.totalZarFormatted}`;
      }
    }
    return "";
  };

  const amountErrorMessage = showAmountError
    ? getAmountValidationMessage()
    : "";

  const amountError = Boolean(amountErrorMessage);

  useEffect(() => {
    if (step === "form") {
      setCreatedPayLinkUrl(null);
      setShowAmountError(false);
    }
  }, [step]);

  const handleConfirmCreate = () => {
    if (!businessId) {
      toast({
        description: "Business profile required to create a payment link.",
        variant: "destructive",
      });
      return;
    }
    if (!selectedAsset?.asset) {
      toast({
        description: "Select a crypto asset.",
        variant: "destructive",
      });
      return;
    }
    const ref = reference.trim();
    const dn = displayName(firstName, lastName);
    onStepChange("loading");
    createPayLinkMutation.mutate(
      {
        amount: parseFloat(centsToZarAmountString(fiatZarCents)),
        currency: "ZAR",
        description: ref || `Payment request for ${dn}`,
        reference: ref || dn,
        paymentMethod: "88pay",
        recipientName: firstName.trim(),
        recipientSurname: lastName.trim(),
        cryptoAsset: selectedAsset.asset.toUpperCase(),
      },
      {
        onSuccess: (link) => {
          setCreatedPayLinkUrl(link.payLinkUrl ?? null);
          onStepChange("share");
        },
        onError: (err) => {
          toast({
            description:
              err instanceof Error
                ? err.message
                : "Could not create payment link",
            variant: "destructive",
          });
          onStepChange("failed");
        },
      }
    );
  };

  const shareUrl = createdPayLinkUrl ?? "";
  const shareBody = `Payment link for ${displayName(firstName, lastName)} — ${formatZarDisplay(fiatZar)} (${formatCryptoLine(cryptoAmt, selectedAsset?.asset || "")})\n${reference || "Payment"}\n${shareUrl}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ description: "Link copied to clipboard" });
    } catch {
      toast({ description: "Could not copy link", variant: "destructive" });
    }
  };

  const sendWhatsapp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(shareBody)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const sendEmail = () => {
    const subject = encodeURIComponent(
      `Payment link — ${displayName(firstName, lastName)}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${encodeURIComponent(shareBody)}`;
  };

  const sendSms = () => {
    window.location.href = `sms:?body=${encodeURIComponent(shareBody)}`;
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      await copyLink();
      return;
    }
    try {
      await navigator.share({
        title: "Payment link",
        text: shareBody,
        url: shareUrl,
      });
    } catch {}
  };

  if (step === "loading") {
    return (
      <div className="flex min-h-[700px] flex-1 flex-col px-5 pb-6 pt-5 lg:px-8 lg:pt-8">
        <div className="relative w-full h-[600px] scale-125">
          <CardSpinningLoader logoTopposition="top-[45%]" />
        </div>
      </div>
    );
  }

  if (step === "failed") {
    return (
      <div className="flex min-h-[700px] flex-1 flex-col px-5 pb-6 pt-5 lg:px-8 lg:pt-8">
        <div className="mx-auto flex w-[90%] flex-1 flex-col">
          <div className="relative mb-4 flex h-10 w-full items-center justify-end">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-[48px] w-[48px] rounded-full border-[#EBE9EF]"
              aria-label="Close"
              onClick={onClose}
            >
              <X className="h-[18px] w-[18px] text-[#6D6584]" />
            </Button>
          </div>
          <PaymentFailed
            onTryAgain={() => onStepChange("review")}
            border={false}
            footer={false}
            width="w-full"
          />
        </div>
      </div>
    );
  }

  if (step === "form") {
    return (
      <div className="flex min-h-[700px] flex-1 flex-col px-5 pb-6 pt-5 lg:px-8 lg:pt-8">
        <div className="mx-auto w-[90%] flex-1 rounded-2xl bg-white">
          <div className="relative mb-6 flex justify-between h-10 w-full items-center px-4">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-[48px] w-[48px] rounded-full border-[#EBE9EF]"
              aria-label="Back"
              onClick={onClose}
            >
              <ArrowLeft className="h-[18px] w-[18px] text-[#6D6584]" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-[48px] w-[48px] rounded-full border-[#EBE9EF]"
              aria-label="Close"
              onClick={onClose}
            >
              <X className="h-[18px] w-[18px] text-[#6D6584]" />
            </Button>
          </div>
          <h2 className="text-center text-[32px] font-semibold leading-[40px] text-[#33125F] mb-6">
            New payment link
          </h2>
          <div className="flex w-full flex-col gap-6">
            <div className="w-full">
              <label
                htmlFor="paylink-first"
                className="mb-2 block text-[14px] leading-[20px] text-[#6D6584]"
              >
                First name
              </label>
              <Input
                id="paylink-first"
                value={firstName}
                onChange={(e) => {
                  onFirstNameChange(e.target.value);
                  if (showFormErrors) {
                    setShowFormErrors(false);
                  }
                }}
                className={cn(
                  "h-[56px] w-full rounded-xl text-[#423E51]",
                  firstNameErrorMessage
                    ? "border-[#EB3366]"
                    : "border-[#CECBD7]"
                )}
                placeholder=""
                autoComplete="given-name"
                aria-invalid={Boolean(firstNameErrorMessage)}
              />
              {firstNameErrorMessage && (
                <p className="mt-2 text-sm text-[#EB3366]">
                  {firstNameErrorMessage}
                </p>
              )}
            </div>
            <div className="w-full">
              <label
                htmlFor="paylink-last"
                className="mb-2 block text-[14px] leading-[20px] text-[#6D6584]"
              >
                Last name
              </label>
              <Input
                id="paylink-last"
                value={lastName}
                onChange={(e) => {
                  onLastNameChange(e.target.value);
                  if (showFormErrors) {
                    setShowFormErrors(false);
                  }
                }}
                className={cn(
                  "h-[56px] w-full rounded-xl text-[#423E51]",
                  lastNameErrorMessage ? "border-[#EB3366]" : "border-[#CECBD7]"
                )}
                placeholder=""
                autoComplete="family-name"
                aria-invalid={Boolean(lastNameErrorMessage)}
              />
              {lastNameErrorMessage && (
                <p className="mt-2 text-sm text-[#EB3366]">
                  {lastNameErrorMessage}
                </p>
              )}
            </div>
            <div className="w-full">
              <label
                htmlFor="paylink-ref"
                className="mb-2 block text-[14px] leading-[20px] text-[#6D6584]"
              >
                Reference
              </label>
              <div className="relative w-full">
                <Input
                  id="paylink-ref"
                  value={reference}
                  onChange={(e) => onReferenceChange(e.target.value)}
                  className="h-[56px] w-full rounded-xl border-[##CECBD7] pr-24 text-[#423E51]"
                  placeholder=""
                />
                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#6D6584] font-semibold">
                  Optional
                </span>
              </div>
            </div>
          </div>
          <div className="mt-12 flex w-full justify-center">
            <Button
              type="button"
              disabled={!canContinueForm}
              className={cn(
                "h-12 min-w-[302px] rounded-full px-10 text-[14px] leading-[20px] font-semibold text-white",
                canContinueForm
                  ? "bg-gradient-to-r from-[#57249C] to-[#EB3366] hover:opacity-90"
                  : "cursor-not-allowed bg-[#C4BED4] opacity-90"
              )}
              onClick={() => {
                const hasFormErrors =
                  !isValidNameValue(firstName) || !isValidNameValue(lastName);
                if (hasFormErrors) {
                  setShowFormErrors(true);
                  return;
                }
                setShowFormErrors(false);
                onStepChange("amount");
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "amount") {
    const handleSwap = () => {
      if (splitPaymentContext) {
        setSplitPercentChoice("custom");
      }
      if (!rate || amountNum <= 0) {
        setInputInCrypto((v) => !v);
        return;
      }
      if (inputInCrypto) {
        const fiatCents = Math.round(amountNum * rate * 100 + Number.EPSILON);
        onAmountChange(centsToZarAmountString(fiatCents));
      } else {
        const crypto = amountNum / rate;
        const dec = selectedAsset?.asset?.toUpperCase() === "BTC" ? 6 : 8;
        onAmountChange(truncateDecimals(crypto, dec));
      }
      setInputInCrypto((v) => !v);
    };

    const approxDisplay = rateLoading ? "≈ 0.00" : `≈ ${approxLabel}`;

    const onPayLinkAssetPick = (a: PayLinkCryptoAsset) => {
      onSelectAsset(a);
      setAssetSelectOpen(false);
      setAssetSearchTerm("");
    };

    const canContinueFromAsset = Boolean(selectedAsset) && !cryptoLoading;

    const amountEntrySection = (
      <div className="w-full space-y-2">
        <label
          htmlFor="paylink-amount"
          className="mb-2 block text-[14px] leading-[20px] text-[#6D6584]"
        >
          Enter amount to receive
        </label>
        <div className="space-y-2">
          <div
            className={cn(
              "relative flex h-[70px] items-center gap-4 rounded-2xl border px-6 py-3 transition-colors md:h-[88px]",
              amountError ? "border-[#EB3366]" : "border-[#CECBD7]"
            )}
          >
            <p className="shrink-0 text-[24px] font-semibold text-[#6D6584]">
              {inputInCrypto
                ? (selectedAsset?.asset.toUpperCase() ?? "")
                : (fiatPair?.asset?.toUpperCase() ?? "R")}
            </p>
            <input
              id="paylink-amount"
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                const raw = e.target.value;
                if (/^\d*\.?\d*$/.test(raw)) {
                  onAmountChange(raw);
                  if (splitPaymentContext) {
                    setSplitPercentChoice("custom");
                  }
                  if (showAmountError) {
                    setShowAmountError(false);
                  }
                }
              }}
              className="min-w-0 w-[60%] flex-1 bg-transparent text-lg font-semibold text-purple-900 outline-none transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-0"
              placeholder="0"
              aria-invalid={amountError}
            />
            <div className="min-w-0 shrink text-sm text-text-secondary md:text-base">
              {approxDisplay}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2 sm:justify-start">
              <Button
                type="button"
                onClick={handleSwap}
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full border border-neutral md:h-auto md:w-auto"
                aria-label="Swap crypto and ZAR input"
              >
                <ArrowUpDown className="h-3 w-3 text-text-secondary" />
              </Button>
            </div>
          </div>
          {amountError && (
            <p className="text-sm text-[#EB3366]">{amountErrorMessage}</p>
          )}
        </div>
        {splitPaymentContext ? (
          <div className="w-full space-y-2 pt-2">
            <span className="block text-[14px] leading-[20px] text-[#6D6584]">
              Please select the percentage to split
            </span>
            <div className="flex flex-wrap gap-2">
              {SPLIT_PRESET_PERCENTS.map((p) => {
                const selected =
                  splitPercentChoice !== "custom" && splitPercentChoice === p;
                return (
                  <button
                    key={p}
                    type="button"
                    className={cn(
                      "min-h-[48px] rounded-full border px-4 py-2 text-[14px] font-semibold transition-colors",
                      selected
                        ? "border-transparent bg-gradient-to-r from-[#57249C] to-[#EB3366] text-white"
                        : "border-[#EBE9EF] bg-white text-[#423E51] hover:bg-[#F5F4F7]"
                    )}
                    onClick={() => {
                      setSplitPercentChoice(p);
                      setInputInCrypto(false);
                      onAmountChange(
                        amountFromPercentOfTotal(
                          splitPaymentContext.totalZar,
                          p
                        )
                      );
                    }}
                  >
                    {p}%
                  </button>
                );
              })}
              <button
                type="button"
                className={cn(
                  "min-h-[40px] rounded-full border px-4 py-2 text-[14px] font-semibold transition-colors",
                  splitPercentChoice === "custom"
                    ? "border-transparent bg-gradient-to-r from-[#57249C] to-[#EB3366] text-white"
                    : "border-[#EBE9EF] bg-white text-[#423E51] hover:bg-[#F5F4F7]"
                )}
                onClick={() => setSplitPercentChoice("custom")}
              >
                Custom
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );

    return (
      <div className="flex min-h-[700px] flex-1 flex-col px-5 pb-6 pt-5 lg:px-8 lg:pt-8">
        <div className="mx-auto w-[90%] flex-1 rounded-2xl bg-white">
          <p className="text-[24px] leading-[28px] font-semibold text-[#33125F] text-center mt-10 mb-3">
            Amount Due
          </p>
          <p className="mb-6 text-center text-[14px] leading-[20px] text-[#423E51]">
            {amountPhase === "asset"
              ? "Select an asset and enter the amount due"
              : "Please enter the amount due"}
          </p>
          <div className="flex w-full flex-col gap-6">
            {amountPhase === "asset" ? (
              <>
                <div className="w-full space-y-2">
                  <label className="text-[14px] leading-[20px] text-[#6D6584]">
                    Select asset
                  </label>
                  {cryptoLoading ? (
                    <Skeleton className="h-[70px] w-full rounded-2xl md:h-[88px]" />
                  ) : isMobile ? (
                    <AssetSelectionDialog
                      selectedAsset={selectedAsset ?? undefined}
                      searchTerm={assetSearchTerm}
                      onSearchChange={setAssetSearchTerm}
                      filteredAssets={filteredPayLinkAssets}
                      onAssetSelect={(a) =>
                        onPayLinkAssetPick(a as PayLinkCryptoAsset)
                      }
                      showBalance={false}
                      open={assetSelectOpen}
                      onOpenChange={setAssetSelectOpen}
                      isLoading={cryptoLoading}
                    />
                  ) : (
                    <AssetSelectionDropdown
                      selectedAsset={selectedAsset ?? undefined}
                      searchTerm={assetSearchTerm}
                      onSearchChange={setAssetSearchTerm}
                      filteredAssets={filteredPayLinkAssets}
                      onAssetSelect={(a) =>
                        onPayLinkAssetPick(a as PayLinkCryptoAsset)
                      }
                      showBalance={false}
                      open={assetSelectOpen}
                      onOpenChange={setAssetSelectOpen}
                      isLoading={cryptoLoading}
                    />
                  )}
                </div>
                {amountEntrySection}
              </>
            ) : (
              <>
                <RequestingFromField
                  businessName={resolvedBusinessName}
                  showSkeleton={showRequestingSkeleton}
                />
                <button
                  type="button"
                  className="-mt-2 self-start text-left text-[14px] font-semibold text-[#57249C] hover:underline"
                  onClick={() => setAmountPhase("asset")}
                >
                  Change asset
                </button>
                {amountEntrySection}
              </>
            )}
          </div>
          <div className="mt-12 flex w-full justify-center">
            {amountPhase === "asset" ? (
              <Button
                type="button"
                disabled={!canContinueFromAsset}
                className={cn(
                  "h-[56px] min-w-[302px] rounded-full px-10 text-[14px] leading-[20px] font-semibold text-white",
                  canContinueFromAsset
                    ? "bg-gradient-to-r from-[#57249C] to-[#EB3366] hover:opacity-90"
                    : "cursor-not-allowed bg-[#C4BED4] opacity-90"
                )}
                onClick={() => setAmountPhase("details")}
              >
                Continue
              </Button>
            ) : (
              <Button
                type="button"
                className={cn(
                  "h-[56px] min-w-[302px] rounded-full px-10 text-[14px] leading-[20px] font-semibold text-white",
                  canContinueAmount
                    ? "bg-gradient-to-r from-[#57249C] to-[#EB3366] hover:opacity-90"
                    : "cursor-not-allowed bg-[#C4BED4] opacity-90"
                )}
                onClick={() => {
                  if (!canContinueAmount) {
                    setShowAmountError(true);
                    return;
                  }
                  const validationError = getAmountValidationMessage();
                  setShowAmountError(true);
                  if (validationError) {
                    return;
                  }
                  setShowAmountError(false);
                  onStepChange("review");
                }}
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === "review") {
    const amountPrimary = formatZarDisplay(fiatZar);
    const amountSecondary = selectedAsset
      ? formatCryptoLine(cryptoAmt, selectedAsset.asset)
      : "—";

    const splitLabel =
      splitPercentChoice === "custom" ? "Custom" : `${splitPercentChoice}%`;
    const amountRequestedIcon = selectedAsset?.assetIcon || coinsIcon;

    type ReviewRow = { label: string; value: string; sub?: string };
    const reviewRows: ReviewRow[] = [
      { label: "First name", value: firstName.trim() || "—" },
      { label: "Last name", value: lastName.trim() || "—" },
      { label: "Reference", value: reference.trim() || "—" },
    ];

    if (splitPaymentContext) {
      reviewRows.push(
        {
          label: "Total amount",
          value: splitPaymentContext.totalZarFormatted,
        },
        { label: "Split", value: splitLabel },
        {
          label: "Amount requested",
          value: amountPrimary,
          sub: `≈ ${amountSecondary}`,
        }
      );
    } else {
      reviewRows.push({ label: "Amount requested", value: amountPrimary });
    }

    return (
      <div className="flex min-h-[700px] flex-1 flex-col px-5 pb-6 pt-5 lg:px-8 lg:pt-8">
        <div className="mx-auto w-[90%] flex-1 rounded-2xl bg-white">
          <div className="mb-6 rounded-2xl bg-[#EBE9EF4D] h px-4 py-6 text-center">
            <p className="text-[20px] leading-[28px] text-[#6D6584]">
              Amount requested
            </p>
            <div className="flex items-center justify-center mt-2 gap-2">
              <img
                src={amountRequestedIcon}
                alt={selectedAsset?.asset}
                className="w-[40px] h-[40px]"
              />
              <p className="text-[40px] leading-[48px] font-bold text-[#423E51]">
                {amountPrimary}
              </p>
            </div>
            <p className="mt-1 text-sm text-[#6D6584]">{amountSecondary}</p>
          </div>
          <p className="mb-3 text-[14px] leading-[20px] font-semibold text-[#6D6584]">
            Transaction details
          </p>
          <div className="mb-8 w-full overflow-hidden rounded-xl border border-[#EBE9EF]">
            {reviewRows.map((row, i) => (
              <div
                key={row.label}
                className={cn(
                  "flex justify-between gap-4 px-4 py-5 text-sm",
                  row.sub ? "items-start" : "items-center",
                  i < reviewRows.length - 1 && "border-b border-[#EBE9EF]"
                )}
              >
                <span className="text-[#423E51] text-[16px] leading-[24px]">
                  {row.label}
                </span>
                <div className="max-w-[60%] text-right">
                  <span className="font-semibold text-[#423E51] text-[20px] leading-[28px]">
                    {row.value}
                  </span>
                  {row.sub ? (
                    <p className="mt-1 text-[14px] leading-[20px] text-[#6D6584]">
                      {row.sub}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          <div className="flex w-full flex-col items-center gap-4 sm:flex-row sm:justify-center px-6">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-[64px] w-[64px] rounded-full border-[#EBE9EF]"
              aria-label="Back"
              onClick={() => onStepChange("amount")}
            >
              <ArrowLeft className="h-[20px] w-[20px] text-[#6D6584]" />
            </Button>
            <Button
              type="button"
              disabled={!businessId || createPayLinkMutation.isPending}
              className="h-[64px] w-full flex-1 rounded-full bg-gradient-to-r from-[#57249C] to-[#EB3366] px-8 text-[20px] leading-[28px] font-semibold text-white hover:opacity-90 sm:flex-initial"
              onClick={handleConfirmCreate}
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "share") {
    const subtitle = `${displayName(firstName, lastName)}: ${reference.trim() || "Payment"}`;
    return (
      <div className="flex min-h-[700px] flex-1 flex-col px-5 pb-6 pt-5 lg:px-8 lg:pt-8">
        <div className="mx-auto w-full max-w-lg flex-1 rounded-2xl bg-white">
          <div className="mb-6 text-center">
            <p className="text-[20px] leading-[28px] font-semibold text-[#57249C] mb-5">
              Payment link
            </p>
            <p className="text-[24px] leading-[32px] font-semibold text-[#423E51]">
              {formatZarDisplay(fiatZar)}
            </p>
            <p className="mt-1 text-sm text-[#6D6584]">
              {selectedAsset
                ? formatCryptoLine(cryptoAmt, selectedAsset.asset)
                : ""}
            </p>
            <p className="mt-2 text-[16px] leading-[24px] text-[#6D6584]">
              {subtitle}
            </p>
          </div>
          <ul className="flex w-full flex-col gap-3">
            <li className="overflow-hidden rounded-xl border border-[#F1EDF7] min-h-[72px]">
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50"
                onClick={sendWhatsapp}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
                  <img
                    src={whatsappIcon}
                    alt=""
                    className="h-10 w-10 object-contain"
                  />
                </span>
                <span className="min-w-0 flex-1 text-[16px] font-semibold text-[#423E51]">
                  Send via Whatsapp
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#EBE9EF]">
                  <ArrowRight className="h-4 w-4 text-[#6D6584]" />
                </span>
              </button>
            </li>
            <li className="overflow-hidden rounded-xl border border-[#F1EDF7] min-h-[72px]">
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50"
                onClick={sendEmail}
              >
                <span className={shareGradientIcon}>
                  <Mail className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="min-w-0 flex-1 text-[16px] font-semibold text-[#423E51]">
                  Send via Email
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#EBE9EF]">
                  <ArrowRight className="h-4 w-4 text-[#6D6584]" />
                </span>
              </button>
            </li>
            <li className="overflow-hidden rounded-xl border border-[#F1EDF7] min-h-[72px]">
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50"
                onClick={sendSms}
              >
                <span className={shareGradientIcon}>
                  <MessageSquare className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="min-w-0 flex-1 text-[16px] font-semibold text-[#423E51]">
                  Send via SMS
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#EBE9EF]">
                  <ArrowRight className="h-4 w-4 text-[#6D6584]" />
                </span>
              </button>
            </li>
            <li className="overflow-hidden rounded-xl border border-[#F1EDF7] min-h-[72px]">
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50"
                onClick={copyLink}
              >
                <span className={shareGradientIcon}>
                  <Copy className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="min-w-0 flex-1 text-[16px] font-semibold text-[#423E51]">
                  Copy link
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#EBE9EF]">
                  <ArrowRight className="h-4 w-4 text-[#6D6584]" />
                </span>
              </button>
            </li>
            <li className="overflow-hidden rounded-xl border border-[#F1EDF7] min-h-[72px]">
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50"
                onClick={nativeShare}
              >
                <span className={shareGradientIcon}>
                  <Share2 className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="min-w-0 flex-1 text-[16px] font-semibold text-[#423E51]">
                  Share
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#EBE9EF]">
                  <ArrowRight className="h-4 w-4 text-[#6D6584]" />
                </span>
              </button>
            </li>
          </ul>
          <div className="mt-12 flex w-full justify-center">
            <Button
              type="button"
              className="h-12 w-full max-w-xs rounded-full bg-gradient-to-r from-[#57249C] to-[#EB3366] px-10 text-base font-semibold text-white hover:opacity-90"
              onClick={onClose}
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
